import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Receipt, Mail, Phone, Globe } from 'lucide-react';
import { useLegal, CompanyName } from '../../components/common/LegalCompany';

const SECTIONS = [
  {
    title: '1. Subscription Payments',
    body: (
      <p>All Cable TV subscription charges, recharge payments, installation charges, activation charges, and other service-related payments are subject to this Refund Policy.</p>
    ),
  },
  {
    title: '2. Non-Refundable Payments',
    body: (
      <>
        <p>Unless otherwise required by applicable law, the following payments are <strong>non-refundable</strong>:</p>
        <ul>
          <li>Monthly, quarterly, half-yearly, and yearly Cable TV subscription charges.</li>
          <li>Recharge payments after successful activation or renewal.</li>
          <li>Installation charges.</li>
          <li>Activation charges.</li>
          <li>Service visit charges.</li>
          <li>Charges for premium channels or add-on channel packages that have already been activated.</li>
        </ul>
      </>
    ),
  },
  {
    title: '3. Duplicate or Incorrect Payments',
    body: (
      <>
        <p>If you accidentally make a duplicate payment or are charged incorrectly due to a technical error, please contact us within <strong>7 days</strong> of the transaction.</p>
        <p>After verification, any eligible excess amount may be:</p>
        <ul>
          <li>Refunded to the original payment method, or</li>
          <li>Adjusted towards your future Cable TV subscription.</li>
        </ul>
        <p>The method of settlement will be determined by <CompanyName />.</p>
      </>
    ),
  },
  {
    title: '4. Failed Transactions',
    body: (
      <>
        <p>If your payment is debited but your Cable TV recharge or subscription is not activated due to a technical issue:</p>
        <ul>
          <li>Please allow up to <strong>7 business days</strong> for your bank or payment gateway to process an automatic reversal.</li>
          <li>If the issue remains unresolved, please contact us with your payment details for verification.</li>
        </ul>
      </>
    ),
  },
  {
    title: '5. Service Cancellation',
    body: (
      <>
        <p>Customers may request disconnection of their Cable TV connection at any time.</p>
        <p>However:</p>
        <ul>
          <li>No refund will be provided for any unused portion of an active subscription period.</li>
          <li>Any outstanding dues must be cleared before the service is disconnected.</li>
        </ul>
      </>
    ),
  },
  {
    title: '6. Set Top Box (STB)',
    body: (
      <>
        <p>Any Set Top Box (STB), remote, smart card, or other equipment provided on a <strong>Free-to-Use</strong>, rental, or returnable basis remains the property of <CompanyName /> unless otherwise specified.</p>
        <p>The equipment must be returned in good working condition at the time of disconnection. Failure to return company-owned equipment may result in applicable recovery or replacement charges.</p>
      </>
    ),
  },
  {
    title: '7. Refund Processing',
    body: (
      <p>Where a refund is approved, it will generally be processed within <strong>7 to 14 business days</strong>, depending on the payment method and banking procedures.</p>
    ),
  },
  {
    title: '8. Exceptions',
    body: (
      <>
        <p>Refunds may be considered only in exceptional circumstances, including:</p>
        <ul>
          <li>Duplicate payments.</li>
          <li>Incorrect billing due to our error.</li>
          <li>Technical issues resulting in unsuccessful service activation despite successful payment.</li>
          <li>Any other case where <CompanyName /> determines that a refund is appropriate.</li>
        </ul>
        <p>All refund requests are subject to verification and approval.</p>
      </>
    ),
  },
];

export default function RefundPolicyPage() {
  const legal = useLegal();
  const navigate = useNavigate();

  return (
    <div className="refund-page">
      <style>{`
        .refund-page { min-height: 100vh; background: #f8fafc; padding-bottom: 48px; }
        .refund-header {
          background: radial-gradient(circle at 50% 0%, #1e3a8a 0%, #0c1a3d 55%, #060e24 100%);
          padding: 22px 20px calc(28px + env(safe-area-inset-top, 0px));
          padding-top: calc(22px + env(safe-area-inset-top, 0px));
          color: #fff;
        }
        .refund-header-top { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; }
        .refund-back-btn {
          display: flex; align-items: center; justify-content: center; width: 34px; height: 34px;
          border-radius: 10px; border: 1px solid rgba(255,255,255,.3); background: rgba(255,255,255,.08);
          color: #fff; cursor: pointer;
        }
        .refund-title-row { display: flex; align-items: center; gap: 10px; }
        .refund-title { font-size: 1.3rem; font-weight: 800; margin: 0; }
        .refund-subtitle { font-size: .8rem; color: rgba(255,255,255,.65); margin-top: 4px; }
        .refund-body { max-width: 760px; margin: -14px auto 0; padding: 0 16px; }
        .refund-card {
          background: #fff; border-radius: 16px; border: 1px solid #e5e7eb;
          box-shadow: 0 8px 24px rgba(0,0,0,.06); padding: 24px 22px; margin-bottom: 18px;
        }
        .refund-card h3 { font-size: 1.02rem; font-weight: 800; color: #111827; margin: 0 0 10px; }
        .refund-card p { font-size: .88rem; line-height: 1.65; color: #374151; margin: 0 0 8px; }
        .refund-card ul { margin: 0 0 8px; padding-left: 20px; }
        .refund-card li { font-size: .88rem; line-height: 1.65; color: #374151; margin-bottom: 3px; }
        .refund-contact-item { display: flex; align-items: center; gap: 8px; font-size: .85rem; color: #374151; margin-bottom: 8px; }
        .refund-contact-item a { color: var(--primary, #1e3a8a); text-decoration: none; font-weight: 600; }
      `}</style>

      <div className="refund-header">
        <div className="refund-header-top">
          <button type="button" className="refund-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowLeft size={17} />
          </button>
        </div>
        <div className="refund-title-row">
          <Receipt size={22} />
          <h1 className="refund-title">Refund Policy</h1>
        </div>
        <div className="refund-subtitle">{legal.name} &middot; applies to {legal.portalLabel}</div>
      </div>

      <div className="refund-body">
        <div className="refund-card">
          <p style={{ marginBottom: 0 }}>
            At <strong>{legal.name}</strong>, we are committed to providing reliable Cable TV services. This Refund
            Policy outlines the terms governing subscription payments, cancellations, and refunds for our Cable TV
            services. By subscribing to or recharging your Cable TV service, you agree to this Refund Policy.
          </p>
        </div>

        {SECTIONS.map((s) => (
          <div className="refund-card" key={s.title}>
            <h3>{s.title}</h3>
            {s.body}
          </div>
        ))}

        <div className="refund-card">
          <h3>9. Contact Us</h3>
          <p>If you have any questions regarding this Refund Policy or wish to request a refund, please contact us:</p>
          <p style={{ fontWeight: 700, color: '#111827', marginBottom: 10 }}>{legal.name}</p>
          <div className="refund-contact-item">
            <Globe size={14} />
            <a href={legal.portalUrl} target="_blank" rel="noreferrer">{legal.portalLabel}</a>
          </div>
          {legal.email && (
          <div className="refund-contact-item">
            <Mail size={14} />
            <a href={`mailto:${legal.email}`}>{legal.email}</a>
          </div>
          )}
          <div className="refund-contact-item" style={{ marginBottom: 0 }}>
            <Phone size={14} />
            <a href={`tel:${legal.phoneHref}`}>{legal.phone}</a>
          </div>
          <p style={{ marginTop: 10, marginBottom: 0 }}>We will review your request and respond as soon as reasonably possible.</p>
        </div>

        <div style={{ textAlign: 'center', fontSize: '.78rem', color: '#94a3b8' }}>
          <Link to="/portal/login" style={{ color: 'var(--primary, #1e3a8a)', fontWeight: 600, textDecoration: 'none' }}>
            &larr; Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
